import {
    ICorporationResponse,
    ILocationResponse,
} from '@kcftech/prometheustypescriptapiclient';
import { FileUploadButton } from 'components/button/FileUploadButton';
import { useNotificationContext } from 'contexts/NotificationContext';
import { useFileUpload } from 'pages/Settings/FileLibrary/useFileUpload';
import { useRef } from 'react';
import { isFileAJpeg, normalizeImageFileRotation } from 'utils/file';

export type MultiFileUploadProps = Readonly<{
    /**
     * upload callback
     */
    onUpload: () => void;
    /**
     * The current corporation
     */
    corporation?: ICorporationResponse;
    /**
     * The current location
     */
    location?: ILocationResponse;
    /**
     * The groups ids used to associate groups to the file(s)
     */
    groupIds?: string[];

    /**
     * Set the upload to be multi or single file
     */
    isMultiFile?: boolean;

    /**
     * Set the button spacing; none, normal, medium, large, undefined
     */
    buttonSpacing?: 'none' | 'medium' | 'compact' | 'large' | undefined;

    /**
     * Id to set as the ParentFolderId for the uploaded file
     */
    parentFolderId?: string;
}>;

export function MultiFileUpload({
    onUpload,
    location,
    corporation,
    groupIds,
    isMultiFile = true,
    buttonSpacing = 'medium',
    parentFolderId,
}: MultiFileUploadProps) {
    const fileInputRef = useRef<HTMLInputElement>(null);

    const { showToast } = useNotificationContext();
    const { mutate: uploadFile, isError, error, isPending } = useFileUpload(
        {
            location,
            corporation,
            groupIds,
            parentFolderId,
        },
        {
            onSuccess: (data) => {
                onUpload();
                showToast({
                    type: 'success',
                    content: `${data.DisplayName} uploaded successfully.`,
                });
            },
            onError: (_e, data) => {
                showToast({
                    type: 'error',
                    content: `An error occurred while uploading file: ${data.name}.`,
                });
            },
        }
    );

    const onUploadInputChange = () => {
        if (fileInputRef.current) {
            const { files } = fileInputRef.current;
            const filesList = Array.from(files as ArrayLike<File>);

            filesList.map(async (fileData) => {
                if (isFileAJpeg(fileData)) {
                    const normalizedFile = await normalizeImageFileRotation(fileData);
                    uploadFile({ data: normalizedFile, name: fileData.name });
                } else {
                    uploadFile({ data: fileData, name: fileData.name });
                }
            });

            // Clear the file input so that the onchange will
            // trigger if the same file is selected again
            fileInputRef.current.value = '';
        }
    };

    return (
        <FileUploadButton
            error={error}
            isError={isError}
            fileInputRef={fileInputRef}
            isMultiFile={isMultiFile}
            buttonSpacing={buttonSpacing}
            onUpload={onUploadInputChange}
            onButtonClick={() => fileInputRef.current?.click()}
            isUploading={isPending}
            buttonLabel={`Upload ${isMultiFile ? 'files' : 'file'}`}
            absolutePositionedSpinner
        />
    );
}
